import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, X } from "../../utils/icons";
import { getSocket, emitSocket, onSocket, offSocket, encode, decode } from "../../services/socket";
import { SOCKET_EVENTS } from "../../utils/constants";
import { storage } from "../../utils/storage";
import coins, { formatBalance } from "../../utils/coins";
import DepositModal from "../wallet/DepositModal";

const HeaderCredit = () => {
  const [coin, setCoin] = useState(storage.getKey("coin") || localStorage.getItem("coin") || "INR");
  const [credit, setCredit] = useState(() => storage.getKey("credit") || null);
  const [credits, setCredits] = useState({});
  const [loaded, setLoaded] = useState(!!storage.getKey("credit"));
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [different, setDifferent] = useState([]);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  // Request credit on mount and coin change
  useEffect(() => {
    const token = storage.getKey("token") || localStorage.getItem("token");
    let t = token;
    try { t = JSON.parse(token); } catch {}
    emitSocket(SOCKET_EVENTS.CREDIT, encode({ token: t, coin }));
  }, [coin]);

  // Retry after 4s if credit is still 0 (socket might not have been connected)
  useEffect(() => {
    const retry = setTimeout(() => {
      const token = storage.getKey("token") || localStorage.getItem("token");
      let t = token;
      try { t = JSON.parse(token); } catch {}
      emitSocket(SOCKET_EVENTS.CREDIT, encode({ token: t, coin }));
    }, 4000);
    return () => clearTimeout(retry);
  }, []);


  // Socket listeners
  useEffect(() => {
    const handleCredit = (data) => {
      const decoded = decode(data);
      if (!decoded?.credit) return;
      const creditObj = decoded.credit;
      setCredits(creditObj);
      const activeCoin = coin.toLowerCase();
      const raw = creditObj[activeCoin] ?? 0;
      const formatted = formatBalance(raw, coin);
      setCredit(formatted);
      setLoaded(true);
      storage.setKey("credit", formatted);
    };

    const handleUpdateCredit = (data) => {
      const decoded = decode(data);
      const newVal = Number(decoded.value);
      const oldVal = Number(credit);

      if (Number.isFinite(newVal) && Number.isFinite(oldVal)) {
        const diff = newVal - oldVal;
        if (Math.abs(diff) > 0.00000001) {
          setDifferent((prev) => [
            { amount: Math.abs(diff), color: diff > 0 ? "green" : "red" },
            ...prev,
          ]);
          // Auto-remove after 2s
          setTimeout(() => setDifferent((prev) => prev.slice(0, -1)), 2000);
        }
      }

      const formatted = formatBalance(newVal, coin);
      setCredit(formatted);
      storage.setKey("credit", formatted);
    };

    onSocket(SOCKET_EVENTS.CREDIT, handleCredit);
    onSocket(SOCKET_EVENTS.UPDATE_CREDIT, handleUpdateCredit);

    return () => {
      offSocket(SOCKET_EVENTS.CREDIT, handleCredit);
      offSocket(SOCKET_EVENTS.UPDATE_CREDIT, handleUpdateCredit);
    };
  }, [coin, credit]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectCoin = useCallback((preffix) => {
    if (coin === preffix) return;
    storage.setKey("coin", preffix);
    setCoin(preffix);
    setShowDropdown(false);
    // Update credit from cached credits
    const val = credits[preffix.toLowerCase()] ?? 0;
    setCredit(formatBalance(val, preffix));
    storage.setKey("credit", formatBalance(val, preffix));
    // Re-emit CREDIT socket to get fresh balance for new coin
    const token = storage.getKey("token") || localStorage.getItem("token");
    let t = token;
    try { t = JSON.parse(token); } catch {}
    setTimeout(() => {
      emitSocket(SOCKET_EVENTS.CREDIT, encode({ token: t, coin: preffix }));
    }, 200);
  }, [coin, credits]);

  const isMobile = window.innerWidth <= 768;

  // Build display coins list (exclude SC like old frontend)
  const displayCoins = coins
    .filter((c) => c.active && c.preffix !== "SC")
    .map((c) => ({
      ...c,
      value: formatBalance(credits[c.preffix.toLowerCase()] ?? 0, c.preffix),
      isActive: c.preffix === coin,
    }));

  return (
    <div className="hc-wrapper" ref={wrapperRef}>
      {/* Balance diff animations */}
      {different.length > 0 && (
        <div className="hc-diff-list">
          {different.map((d, i) => (
            <span key={i} className={`hc-diff ${d.color === "green" ? "hc-diff-up" : "hc-diff-down"}`}>
              {d.color === "green" ? "+" : "-"}{formatBalance(d.amount, coin)}
            </span>
          ))}
        </div>
      )}

      {/* Coin selector */}
      <div className="hc-coin-selector" onClick={() => setShowDropdown((v) => !v)}>
        <img
          className="hc-coin-icon"
          src={`/assets/images/${coin.toLowerCase()}.png`}
          alt={coin}
          onError={(e) => { e.target.style.display = "none"; }}
        />
        <span className="hc-credit">
          {!loaded ? (
            <span className="hc-skeleton" />
          ) : (
            ["INR", "BDT", "USDT"].includes(coin)
              ? (Number.isFinite(Number(credit)) ? Number(credit).toFixed(2) : "0.00")
              : (credit && credit !== "NaN" ? credit : "0")
          )}
        </span>
        <ChevronDown size={14} color="#fff" />
      </div>

      {/* Deposit button */}
      <div className="hc-deposit-btn" onClick={() => setShowDepositModal(true)}>
        {isMobile ? <span style={{ fontSize: "18px", fontWeight: "bold" }}>+</span> : "Deposit"}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="hc-dropdown">
          <div className="hc-dropdown-list">
            {displayCoins.map((c) => (
              <div
                key={c.preffix}
                className={`hc-dropdown-item${c.isActive ? " hc-dropdown-item--active" : ""}`}
                onClick={() => selectCoin(c.preffix)}
              >
                <div className="hc-dropdown-item-left">
                  <img
                    className="hc-coin-icon"
                    src={`/assets/images/${c.image}`}
                    alt={c.preffix}
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                  <span>{c.preffix}</span>
                </div>
                <span className="hc-dropdown-item-val">{c.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Deposit Modal */}
      {showDepositModal && (
        <div style={{
          position: "fixed", top: "10%", left: 0, right: 0, bottom: "10%",
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          justifyContent: "center", alignItems: "center", zIndex: 1050, padding: 20,
        }} onClick={(e) => e.target === e.currentTarget && setShowDepositModal(false)}>
          <div style={{
            background: "var(--color-menu-item)", borderRadius: 12, width: "100%", maxWidth: 570,
            minHeight: 400, maxHeight: "76vh", display: "flex", flexDirection: "column",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)",
            animation: "modalFadeIn 0.3s ease-out",
          }}>
            {/* Header */}
            <div style={{
              display: "grid", gridTemplateColumns: "24px 1fr 24px", alignItems: "center",
              padding: "16px 24px", borderBottom: "1px solid #2A2D35",
              background: "var(--color-menu-item-hover)", borderRadius: "12px 12px 0 0",
            }}>
              <span />
              <h4 style={{ color: "#fff", margin: 0, fontSize: 18, fontWeight: 600, textAlign: "center" }}>Deposit</h4>
              <button onClick={() => setShowDepositModal(false)} style={{
                background: "none", border: "none", color: "var(--color-text-muted)", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", marginLeft: "auto",
              }}>
                <X size={24} />
              </button>
            </div>
            {/* Body */}
            <div style={{ padding: "0 24px 24px", color: "#fff", overflowY: "auto", flexGrow: 1, background: "var(--color-menu-item)", borderRadius: "0 0 12px 12px" }}>
              <DepositModal onClose={() => setShowDepositModal(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderCredit;
